/**
 * 极练 Rehab & Fit —— 唯一「术语 → 中文界面文案」映射层（阶段 1b）。
 *
 * 铁律与设计原则：
 * 1. 英文枚举、缩写、内部术语严禁直接展示在用户界面；
 * 2. RIR / targetRir →「余力」；
 * 3. planType → 正常训练 / 今日轻松练 / 今日保守练 / 脚部保护模式 / 恢复周 / 轻量活动日；
 * 4. weightUnitScope → 每只哑铃 / 杠铃总重 / 单侧 / 器械片数 / 自重 / 自重+负重 / 辅助重量；
 * 5. e1RM → 标题统一使用「力量趋势」，「估算极限重量」仅用于详情解释；
 * 6. 「减量周」统一映射为「恢复周」，「准备度」统一映射为「今日状态」；
 * 7. 文案原则：客观、无羞辱感、不制造打卡压力。
 */

import type { RirValue, WeightUnitScope, WorkoutPlanType } from "@jilian/shared";

// ---------------------------------------------------------------------------
// 1. 余力（RIR - Reps in Reserve）映射
// ---------------------------------------------------------------------------

export const RIR_PROMPT_QUESTION = "做完这组还能再做几次？";
export const RIR_HEADER_LABEL = "余力";

export interface RirOption {
  value: RirValue;
  label: string;
  shortLabel: string;
  description: string;
}

export const RIR_OPTIONS: readonly RirOption[] = [
  { value: 0, label: "0 次（已力竭）", shortLabel: "0 (力竭)", description: "一个都做不动了" },
  { value: 1, label: "1 次（接近力竭）", shortLabel: "1 次", description: "拼尽全力还能做 1 次" },
  { value: 2, label: "2 次（刚刚好）", shortLabel: "2 次", description: "保持良好动作形态再做 2 次" },
  { value: 3, label: "3+ 次（还很轻松）", shortLabel: "3+ 次", description: "余力充足，体感轻松" },
] as const;

/** 将 RIR 数值映射为友好中文 */
export function rirLabel(rir: RirValue | number | null | undefined): string {
  if (rir == null) return "未记录";
  if (rir === 0) return "0 次（力竭）";
  if (rir === 1) return "余力 1 次";
  if (rir === 2) return "余力 2 次（合适）";
  if (rir >= 3) return "余力 3+ 次（轻松）";
  return `余力 ${rir} 次`;
}

/** 极简缩写（用于组行紧凑展示） */
export function rirShortLabel(rir: RirValue | number | null | undefined): string {
  if (rir == null) return "—";
  if (rir === 0) return "力竭";
  if (rir >= 3) return "余 3+";
  return `余 ${rir}`;
}

// ---------------------------------------------------------------------------
// 2. 计划类型（planType）映射
// ---------------------------------------------------------------------------

export const PLAN_TYPE_MAP: Record<WorkoutPlanType, { title: string; badge: string; desc: string }> = {
  normal: {
    title: "正常训练",
    badge: "正式训练",
    desc: "状态良好，按标准负荷与双进阶推进",
  },
  lightly_reduced: {
    title: "今日轻松练",
    badge: "轻微减负",
    desc: "感觉轻度疲劳，适当降低组数与强度",
  },
  reduced: {
    title: "今日保守练",
    badge: "明显减负",
    desc: "疲劳明显，减少动作与组数，保留充沛体力",
  },
  injury_reduced: {
    title: "脚部保护模式",
    badge: "避免站姿",
    desc: "足部轻微不适，剔除站姿负重动作，专注坐卧稳定训练",
  },
  injury_conservative: {
    title: "今日保守练",
    badge: "深度保护",
    desc: "足部明显不适，仅保留最高稳定度坐卧器械",
  },
  optional_light_day: {
    title: "轻量活动日",
    badge: "可选活动",
    desc: "主打促进血液循环与肌肉恢复，不冲击极限",
  },
  deload: {
    title: "恢复周",
    badge: "主动减量",
    desc: "系统性降低训练量，消除深层疲劳与中枢压力",
  },
};

export function planTypeTitle(planType: WorkoutPlanType | string | null | undefined): string {
  if (!planType) return "正常训练";
  return PLAN_TYPE_MAP[planType as WorkoutPlanType]?.title ?? "正常训练";
}

export function planTypeBadge(planType: WorkoutPlanType | string | null | undefined): string {
  if (!planType) return "正式训练";
  return PLAN_TYPE_MAP[planType as WorkoutPlanType]?.badge ?? "正式训练";
}

export function planTypeDescription(planType: WorkoutPlanType | string | null | undefined): string {
  if (!planType) return "";
  return PLAN_TYPE_MAP[planType as WorkoutPlanType]?.desc ?? "";
}

// ---------------------------------------------------------------------------
// 3. 重量口径（weightUnitScope）映射
// ---------------------------------------------------------------------------

export const WEIGHT_SCOPE_MAP: Record<WeightUnitScope, { full: string; short: string; unit: string }> = {
  per_dumbbell: { full: "每只哑铃", short: "kg/只", unit: "kg" },
  total: { full: "杠铃总重", short: "kg 总重", unit: "kg" },
  per_side: { full: "单侧挂片", short: "kg/侧", unit: "kg" },
  machine_display: { full: "器械片数", short: "器械", unit: "kg" },
  bodyweight: { full: "自重", short: "自重", unit: "" },
  bodyweight_plus: { full: "自重+负重", short: "自重+", unit: "kg" },
  assistance: { full: "辅助重量", short: "辅助", unit: "kg" },
};

export function weightScopeLabel(scope: WeightUnitScope | string | null | undefined): string {
  if (!scope) return "kg";
  return WEIGHT_SCOPE_MAP[scope as WeightUnitScope]?.full ?? scope;
}

export function weightScopeShortLabel(scope: WeightUnitScope | string | null | undefined): string {
  if (!scope) return "";
  return WEIGHT_SCOPE_MAP[scope as WeightUnitScope]?.short ?? scope;
}

// ---------------------------------------------------------------------------
// 4. 力量趋势与估算 1RM（e1RM）
// ---------------------------------------------------------------------------

export const E1RM_TITLE = "力量趋势";
export const E1RM_EXPLANATION = "估算极限重量（仅作为同动作同口径下的趋势参考，不建议进行极限试重）";
export const DELOAD_TERM = "恢复周";
export const READINESS_TERM = "今日状态";

// ---------------------------------------------------------------------------
// 5. 进阶决策中文映射
// ---------------------------------------------------------------------------

export const PROGRESSION_DECISION_MAP: Record<
  string,
  { label: string; tone: "good" | "warn" | "danger" | "accent" }
> = {
  increase: { label: "下次加重", tone: "good" },
  maintain: { label: "维持重量", tone: "accent" },
  decrease: { label: "下次降重", tone: "warn" },
  recalibrate: { label: "试重评估", tone: "warn" },
  replace: { label: "建议替换", tone: "danger" },
  no_decision: { label: "暂不判定", tone: "accent" },
};

// ---------------------------------------------------------------------------
// 6. 同步与网络状态映射
// ---------------------------------------------------------------------------

export const SYNC_STATUS_MAP: Record<string, string> = {
  local_only: "本地保存",
  pending: "待同步",
  syncing: "正在同步…",
  synced: "已同步",
  conflict: "版本保留",
  failed: "同步未完成",
};

export function syncStatusLabel(status: string | null | undefined): string {
  if (!status) return "本地";
  return SYNC_STATUS_MAP[status] ?? status;
}

// ---------------------------------------------------------------------------
// 7. 疲劳与恢复术语映射
// ---------------------------------------------------------------------------

export const FATIGUE_INDEX_TITLE = "中枢疲劳指数";
export const ATL_TITLE = "近期负荷 (ATL)";
export const ATL_EXPLANATION = "过去 7 天加权训练负荷";
export const CTL_TITLE = "长期体能 (CTL)";
export const CTL_EXPLANATION = "过去 28 天体能积累基准";
export const TSB_TITLE = "恢复平衡 (TSB)";
export const TSB_EXPLANATION = "体能与疲劳平衡（正值体能充沛，负值疲劳积累）";

